import { useState, type FormEvent, type ReactElement } from 'react';
import { AuthApi, isSecondFactorChallenge } from './AuthApi';
import { authProblem, destinationLabel } from './authModel';
import type { AuthUser, SecondFactorChallenge } from './types';

interface LoginScreenProps {
  api: AuthApi;
  path: string;
  notice: string | null;
  onAuthenticated: (user: AuthUser) => void;
}

export function LoginScreen({ api, path, notice, onAuthenticated }: LoginScreenProps): ReactElement {
  const [email, setEmail] = useState(readRememberedEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [challenge, setChallenge] = useState<SecondFactorChallenge | null>(null);
  const [code, setCode] = useState('');
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitPassword(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const result = await api.login(email.trim(), password);
      rememberEmail(email.trim());
      setPassword('');
      if (isSecondFactorChallenge(result)) {
        setChallenge(result);
        setCode('');
      } else {
        onAuthenticated(result.user);
      }
    } catch (reason: unknown) {
      setError(authProblem(reason));
    } finally {
      setPending(false);
    }
  }

  async function submitSecondFactor(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending || challenge === null) return;
    setPending(true);
    setError(null);
    try {
      const result = await api.verifySecondFactor(challenge.challengeToken, code.trim());
      setCode('');
      onAuthenticated(result.user);
    } catch (reason: unknown) {
      setError(authProblem(reason));
    } finally {
      setPending(false);
    }
  }

  return <main className="auth-page">
    <section className="auth-context" aria-label="SweetLana">
      <a className="auth-wordmark" href="/">RADIO <span>PLATFORM</span></a>
      <div className="auth-context-copy">
        <p className="eyebrow">ЛОКАЛЬНА ПАНЕЛЬ ЕФІРУ</p>
        <h1>Керуйте ефіром.<br />Сервер продовжує мовлення.</h1>
        <p>Вхід належить цій інсталяції. Пароль і другий фактор не передаються стороннім сервісам.</p>
      </div>
      <dl className="auth-facts">
        <div><dt>Станція</dt><dd><i /> Автоефір незалежний від браузера</dd></div>
        <div><dt>Сесія</dt><dd>Захищена · HttpOnly · SameSite</dd></div>
        <div><dt>Призначення</dt><dd>{destinationLabel(path)}</dd></div>
      </dl>
    </section>

    <section className="auth-panel" aria-labelledby="auth-title">
      <div className="auth-form-wrap">
        {challenge === null ? <form className="auth-form" onSubmit={(event) => void submitPassword(event)}>
          <div className="auth-form-heading">
            <p className="eyebrow">ЗАХИЩЕНИЙ ВХІД</p>
            <h2 id="auth-title">Увійдіть до платформи</h2>
            <p>Адміністратор і ведучий використовують власні облікові записи.</p>
          </div>
          {notice !== null && <div className="auth-notice" role="status">{notice}</div>}
          {error !== null && <div className="auth-error" role="alert"><strong>Вхід не виконано</strong><span>{error}</span></div>}
          <label htmlFor="auth-email">Електронна пошта</label>
          <input id="auth-email" name="email" type="email" autoComplete="username" inputMode="email" required autoFocus value={email} onChange={(event) => setEmail(event.target.value)} />
          <label htmlFor="auth-password">Пароль</label>
          <div className="password-field">
            <input id="auth-password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required minLength={1} value={password} onChange={(event) => setPassword(event.target.value)} />
            <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Сховати пароль' : 'Показати пароль'}>{showPassword ? 'СХОВАТИ' : 'ПОКАЗАТИ'}</button>
          </div>
          <button className="auth-submit" type="submit" disabled={pending}>{pending ? 'ПЕРЕВІРЯЮ…' : 'УВІЙТИ'}</button>
          <small>Сесію буде автоматично завершено після строку безпеки.</small>
        </form> : <form className="auth-form" onSubmit={(event) => void submitSecondFactor(event)}>
          <div className="auth-form-heading">
            <p className="eyebrow">ДРУГИЙ ФАКТОР</p>
            <h2 id="auth-title">Підтвердьте вхід</h2>
            <p>{recoveryMode ? 'Введіть один невикористаний резервний код.' : 'Введіть 6-значний код із застосунку-автентифікатора.'}</p>
          </div>
          {error !== null && <div className="auth-error" role="alert"><strong>Код не прийнято</strong><span>{error}</span></div>}
          <label htmlFor="auth-code">{recoveryMode ? 'Резервний код' : 'Код автентифікатора'}</label>
          <input className={recoveryMode ? '' : 'totp-input'} id="auth-code" name="code" type="text" autoComplete="one-time-code" inputMode={recoveryMode ? 'text' : 'numeric'} required autoFocus value={code} onChange={(event) => setCode(event.target.value)} placeholder={recoveryMode ? 'xxxx-xxxx-xxxx' : '000 000'} />
          <button className="auth-submit" type="submit" disabled={pending}>{pending ? 'ПЕРЕВІРЯЮ…' : 'ПІДТВЕРДИТИ'}</button>
          <div className="auth-alternatives">
            <button type="button" onClick={() => { setRecoveryMode((value) => !value); setCode(''); setError(null); }}>{recoveryMode ? 'Використати код із застосунку' : 'Використати резервний код'}</button>
            <button type="button" onClick={() => { setChallenge(null); setCode(''); setError(null); }}>Назад до пароля</button>
          </div>
        </form>}
      </div>
    </section>
  </main>;
}

function readRememberedEmail(): string {
  try { return localStorage.getItem('radio.auth.email') ?? ''; } catch { return ''; }
}

function rememberEmail(email: string): void {
  try { localStorage.setItem('radio.auth.email', email); } catch { /* private storage can be unavailable */ }
}
